import React, { useState, useContext, useEffect, useRef } from "react"; // Add useEffect import
import { Link } from "react-router-dom";
import { Alert } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import Header from "../Dashboard/Header/Header";
import Footer from "../Dashboard/Footer/Footer";
import ContactForm from "../../Shared/ContactForm/ContactForm";
import DraggableHandle from "../../Desktop/DraggableHandle";
import "./Customising.css";
import {
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

// Import constants instead of hardcoding
import {
  DOOR_STYLES,
  DOOR_SERIES,
  ESSENTIAL_DOORS,
  NATIVE_DOORS,
  PANEL_STYLES,
  ALUMICOMP_DESIGNS,
  SOLID_SHEET_DESIGNS,
  COLOURBOND_COLORS,
  PLATINUM_COLORS,
  NATIVE_COLORS,
  COMPOSITE_COLORS,
  MATERIALS,
  SOLID_MATERIALS,
  HAMPTON_WINDOWS,
} from "../../../Constants/constants";

// Import custom hooks
import { useMobileDoorLogic } from "../../../Hooks/useMobileDoorLogic";
import { useMobileImageHandling } from "../../../Hooks/useMobileImageHandling";
import { useMobileDragging } from "../../../Hooks/useMobileDragging";
import { useAnalytics } from "../../../Hooks/useAnalytics";

import { ImageContext } from "../../Context/ImageContext";

function CustomisingDoor() {
  const formRef = useRef(null);

  const { selectedDoor } = useContext(ImageContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState("");

  // Custom hooks
  const doorLogic = useMobileDoorLogic(selectedDoor);
  const { trackVisualiserSave, trackVisualiserQuote } = useAnalytics();
  const imageHandling = useMobileImageHandling(
    doorLogic.points,
    doorLogic.image,
    doorLogic.bgImage
  );
  const dragging = useMobileDragging(
    doorLogic.points,
    doorLogic.setPoints,
    doorLogic.image
  );

  // Handle touch events on the door overlay properly
  useEffect(() => {
    const doorElement = document.querySelector("#door-overlay");
    if (doorElement && dragging.handleTouchStartOnDoor) {
      // Register touch events as non-passive so preventDefault works
      doorElement.addEventListener(
        "touchstart",
        dragging.handleTouchStartOnDoor,
        {
          passive: false,
        }
      );

      return () => {
        doorElement.removeEventListener(
          "touchstart",
          dragging.handleTouchStartOnDoor
        );
      };
    }
  }, [dragging.handleTouchStartOnDoor, doorLogic.image]);

  useEffect(() => {
    if (isModalOpen && formRef.current) {
      formRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isModalOpen]);

  // Handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleColorChange = (e) => {
    doorLogic.handleColorChange(e.target.value);
  };

  const handleWindowChange = (e) => {
    doorLogic.setSelectWindow(e.target.value);
  };

  const handleWidthChange = (e) => {
    doorLogic.setDoorWidth(e.target.value);
  };

  const ColorLabel = ({ name, color, trademark }) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          backgroundColor: color,
          border: "1px solid #ccc",
          marginRight: 1,
        }}
      />
      <Typography variant="body2">
        {name}
        {trademark && (
          <sup style={{ fontSize: "0.7em", marginLeft: "2px" }}>
            {trademark}
          </sup>
        )}
      </Typography>
    </Box>
  );

  useEffect(() => {
    let defaultColor = "#5e1d0e";
    let colorGroup = COLOURBOND_COLORS;

    // Override if Native
    if (doorLogic.selectSeries === "Native") {
      defaultColor = "#632b0a";
      colorGroup = [...NATIVE_COLORS, ...PLATINUM_COLORS];
    }

    // Override if Selections Series and not SolidSheet
    else if (
      doorLogic.selectSeries === "Selections Series" &&
      doorLogic.PanelStyle !== "SolidSheet"
    ) {
      defaultColor = "#9b8c50";
      colorGroup = COMPOSITE_COLORS;
    }

    // Override if Platinum applicable (Madison/Tuscan design)
    else if (
      (doorLogic.selectedDesign === "Madison" ||
        doorLogic.selectedDesign === "Tuscan") &&
      doorLogic.selectSeries !== "Selections Series"
    ) {
      defaultColor = "#3f3f3b";
      colorGroup = PLATINUM_COLORS;
    }

    // Find color in selected group
    const defaultColorObj = colorGroup.find((c) => c.bgcolor === defaultColor);

    if (defaultColorObj) {
      doorLogic.setSelectedColor(defaultColor);
      // doorLogic.selectedColorName(defaultColorObj.name);
    }
  }, [
    doorLogic.selectSeries,
    doorLogic.PanelStyle,
    doorLogic.selectedDesign,
    doorLogic.selectedPattern,
    doorLogic.selectedRect,
  ]);

  return (
    <>
      <Header />
      <section className="customisingDoor mt-4 mb-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <h1>Customising Your Door</h1>
              <p className="door-text mt-4">Door Details</p>

              {!doorLogic.bgImage ? (
                <Link to="/visualiser" className="text-decoration-none">
                  <p className="upload-btn">
                    <CloudUploadIcon /> Select Image
                  </p>
                </Link>
              ) : (
                <div
                  ref={dragging.containerRef}
                  className="overflow-hidden position-relative"
                  id="image-display-area"
                  style={{ width: "100%", height: "300px" }}
                >
                  {/* Background Image */}
                  {doorLogic.bgImage && (
                    <img
                      src={doorLogic.bgImage}
                      alt="Background"
                      onLoad={(e) => {
                        const target = e.target;
                        imageHandling.setHouseImageDimensions({
                          width: target.naturalWidth,
                          height: target.naturalHeight,
                        });
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                      }}
                    />
                  )}

                  {/* Door Overlay - UPDATED */}
                  {doorLogic.image && (
                    <div
                      id="door-overlay" // Added id for touch event handling
                      className="position-absolute"
                      style={{
                        width: "225px",
                        height: "100px",
                        transformOrigin: "0 0",
                        transform: dragging.imageTransform,
                        backfaceVisibility: "hidden",
                        willChange: "transform",
                        cursor: dragging.isDraggingDoor ? "grabbing" : "grab",
                        top: 0,
                        touchAction: "none", // Prevents default touch behaviors
                      }}
                      onMouseDown={dragging.handleMouseDownOnDoor}
                      // Note: onTouchStart is handled via useEffect above for non-passive registration
                    >
                      <img
                        src={doorLogic.image}
                        alt="Selected door"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          pointerEvents: "none",
                          zIndex: 10,
                        }}
                      />
                    </div>
                  )}

                  {/* Draggable Handles */}
                  {dragging.showHandles &&
                    doorLogic.image &&
                    doorLogic.points.map((point, index) => (
                      <DraggableHandle
                        key={index}
                        id={index}
                        x={point.x}
                        y={point.y}
                        onMouseDown={dragging.handleMouseDownOnHandle}
                        size={24}
                      />
                    ))}
                </div>
              )}

              {/* Door Width Selection */}
              <h5 className="mt-3">Select your door width</h5>
              <div className="selected mb-3">
                <select
                  value={doorLogic.doorWidth}
                  onChange={handleWidthChange}
                  className="color-select"
                >
                  <option value="double door">Double Door</option>
                  <option value="single door">Single Door</option>
                </select>
              </div>

              {/* Current Selections Display */}
              <div className="selected mb-3">
                <p className="mb-0 text-capitalize">
                  Door Style - {doorLogic.selectedPattern}
                </p>
              </div>
              <div className="selected mb-3">
                <p className="mb-0 text-capitalize">
                  Colour - Colorbond - {doorLogic.selectedColorName}
                </p>
              </div>

              <Link to="/visualiser">
                <button
                  className="btn start mb-3"
                  onClick={doorLogic.resetToDefault}
                >
                  Start Again
                </button>
              </Link>

              {/* Door Style Selection */}
              <h4 className="mb-3">Select Door Style</h4>
              {DOOR_STYLES.map((pattern) => (
                <div
                  key={pattern.id}
                  className="d-flex justify-content-between align-items-center selected-btn selected mb-2"
                  onClick={() =>
                    document.getElementById(`pattern-${pattern.name}`).click()
                  }
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={pattern.img}
                      alt={pattern.name}
                      className="img-fluid doorstyle-img me-2"
                    />
                    <label htmlFor={`pattern-${pattern.name}`}>
                      {pattern.name.charAt(0).toUpperCase() +
                        pattern.name.slice(1)}
                    </label>
                  </div>
                  <input
                    type="radio"
                    id={`pattern-${pattern.name}`}
                    name="doorStyle"
                    className="me-3"
                    value={pattern.name}
                    checked={doorLogic.selectedPattern === pattern.name}
                    onChange={() => doorLogic.handlePatternChange(pattern.name)}
                  />
                </div>
              ))}

              {/* Door Series Selection */}
              {doorLogic.selectedPattern === "sectional" && (
                <>
                  <h5 className="mt-3">Select Your Door Series</h5>
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    {DOOR_SERIES.map((series) => (
                      <div
                        key={series.id}
                        className="d-grid selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`series-${series.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="series"
                          id={`series-${series.name}`}
                          value={series.name}
                          checked={doorLogic.selectSeries === series.name}
                          onChange={() =>
                            doorLogic.handleSeriesChange(series.name)
                          }
                        />
                        <img
                          src={series.img}
                          alt={series.name}
                          className="img-fluid door-img"
                        />
                        <label
                          htmlFor={`series-${series.name}`}
                          className="label-text"
                        >
                          {series.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Door Design Selection - Essential */}
              {doorLogic.selectSeries === "Essentials" && (
                <>
                  <h5 className="mt-3">Select Your Door Design</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {ESSENTIAL_DOORS.map((design) => (
                      <div
                        key={design.id}
                        className="d-grid selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`design-${design.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="design"
                          id={`design-${design.name}`}
                          value={design.name}
                          checked={doorLogic.selectedDesign === design.name}
                          onChange={() => {
                            doorLogic.setSelectedDesign(design.name);
                            doorLogic.applyDoorDesign(
                              doorLogic.selectedColor,
                              design.name
                            );
                          }}
                        />
                        <img
                          src={design.img}
                          alt={design.name}
                          className="img-fluid door-img"
                        />
                        <label
                          htmlFor={`design-${design.name}`}
                          className="label-text"
                        >
                          {design.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Door Design Selection - Native */}
              {doorLogic.selectSeries === "Native" && (
                <>
                  <h5 className="mt-3">Select Your Door Design</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {NATIVE_DOORS.map((design) => (
                      <div
                        key={design.id}
                        className="d-grid selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`native-design-${design.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="nativeDesign"
                          id={`native-design-${design.name}`}
                          value={design.name}
                          checked={doorLogic.selectedDesign === design.name}
                          onChange={() => {
                            doorLogic.setSelectedDesign(design.name);
                            doorLogic.applyDoorDesign(
                              doorLogic.selectedColor,
                              design.name
                            );
                          }}
                        />
                        <img
                          src={design.img}
                          alt={design.name}
                          className="img-fluid door-img"
                        />
                        <label
                          htmlFor={`native-design-${design.name}`}
                          className="label-text"
                        >
                          {design.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Panel Style Selection - Selections Series */}
              {doorLogic.selectSeries === "Selections Series" && (
                <>
                  <h5 className="mt-3">Select Your Panel Style</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {PANEL_STYLES.map((panel) => (
                      <div
                        key={panel.id}
                        className="d-grid selected-mbl-btn"
                        onClick={() =>
                          document.getElementById(`panel-${panel.name}`).click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="panelStyle"
                          id={`panel-${panel.name}`}
                          value={panel.name}
                          checked={doorLogic.PanelStyle === panel.name}
                          onChange={() => {
                            doorLogic.setPanelStyle(panel.name);
                            if (panel.name === "Alumicomp") {
                              doorLogic.setSelectedDesign("Sovereign");
                              doorLogic.setSelectedMaterial("Composite");
                            }
                          }}
                        />
                        <img
                          src={panel.img}
                          alt={panel.name}
                          className="img-fluid door-img"
                        />
                        <label
                          htmlFor={`panel-${panel.name}`}
                          className="label-text"
                        >
                          {panel.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Alumicomp Design Selection */}
              {doorLogic.PanelStyle === "Alumicomp" && (
                <>
                  <h5 className="mt-3">Select Your Door Design</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {ALUMICOMP_DESIGNS.map((design) => (
                      <div
                        key={design.id}
                        className="d-grid selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`alumicomp-${design.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="alumicompDesign"
                          id={`alumicomp-${design.name}`}
                          value={design.name}
                          checked={doorLogic.selectedDesign === design.name}
                          onChange={() => {
                            doorLogic.setSelectedDesign(design.name);
                          }}
                        />
                        <img
                          src={design.img}
                          alt={design.name}
                          className="img-fluid door-img"
                        />
                        <label
                          htmlFor={`alumicomp-${design.name}`}
                          className="label-text"
                        >
                          {design.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* SolidSheet Design Selection */}
              {doorLogic.PanelStyle === "SolidSheet" && (
                <>
                  <h5 className="mt-3">Select Your Door Design</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {SOLID_SHEET_DESIGNS.map((design) => (
                      <div
                        key={design.id}
                        className="d-grid selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`solid-${design.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="solidDesign"
                          id={`solid-${design.name}`}
                          value={design.name}
                          checked={doorLogic.selectedDesign === design.name}
                          onChange={() => {
                            doorLogic.setSelectedDesign(design.name);
                          }}
                        />
                        <img
                          src={design.img}
                          alt={design.name}
                          className="img-fluid door-img"
                        />
                        <label
                          htmlFor={`solid-${design.name}`}
                          className="label-text"
                        >
                          {design.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Material Selection */}
              {doorLogic.PanelStyle === "Alumicomp" && (
                <>
                  <h5 className="mt-3">Material</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {MATERIALS.map((material) => (
                      <div
                        key={material.id}
                        className="d-grid selected-btn selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`material-${material.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="material"
                          id={`material-${material.name}`}
                          value={material.name}
                          checked={doorLogic.SelectedMaterial === material.name}
                          onChange={() =>
                            doorLogic.setSelectedMaterial(material.name)
                          }
                        />
                        <img
                          src={material.img}
                          alt={material.name}
                          className="img-fluid door-img ms-3"
                        />
                        <label
                          htmlFor={`material-${material.name}`}
                          className="label-text"
                        >
                          {material.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Solid Material Selection */}
              {doorLogic.PanelStyle === "SolidSheet" && (
                <>
                  <h5 className="mt-3">Solid Sheet Material</h5>
                  <div className="d-flex align-items-center flex-wrap mt-2">
                    {SOLID_MATERIALS.map((material) => (
                      <div
                        key={material.id}
                        className="d-grid selected-btn selected-mbl-btn"
                        onClick={() =>
                          document
                            .getElementById(`solidmaterial-${material.name}`)
                            .click()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          className="hidden-radio"
                          hidden
                          type="radio"
                          name="solidMaterial"
                          id={`solidmaterial-${material.name}`}
                          value={material.name}
                          checked={doorLogic.SelectedMaterial === material.name}
                          onChange={() =>
                            doorLogic.setSelectedMaterial(material.name)
                          }
                        />
                        <img
                          src={material.img}
                          alt={material.name}
                          className="img-fluid door-img ms-3"
                        />
                        <label
                          htmlFor={`solidmaterial-${material.name}`}
                          className="label-text"
                        >
                          {material.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {doorLogic.selectSeries !== "Native" &&
                doorLogic.PanelStyle !== "Alumicomp" && (
                  <>
                    <section className="customisingdesktopDoor mt-4 mb-4">
                      <h5 className="mt-3">Select Your Door Colour</h5>
                      <h6>
                        COLORBOND
                        <sup
                          style={{
                            fontSize: "17px",
                            fontWeight: "600",
                            top: "-0.3em",
                          }}
                        >
                          ®
                        </sup>{" "}
                      </h6>
                      <div className="d-flex align-items-center flex-wrap">
                        <div className="selected mb-3 d-flex align-items-center">
                          <FormControl fullWidth>
                            <InputLabel id="colorbond-label">
                              Select a Colour
                            </InputLabel>
                            <Select
                              labelId="colorbond-label"
                              value={doorLogic.selectedColor}
                              label="Colorbond"
                              name="colorbond"
                              onChange={handleColorChange}
                            >
                              {COLOURBOND_COLORS.map((option) => (
                                <MenuItem
                                  key={option.id}
                                  value={option.bgcolor}
                                >
                                  <ColorLabel
                                    name={option.name}
                                    trademark={option.trademark}
                                    color={option.bgcolor}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </section>
                  </>
                )}

              {doorLogic.selectSeries === "Native" && (
                <>
                  <section className="customisingdesktopDoor mt-4 mb-4">
                    <h5 className="mt-3">Select Your Door Colour</h5>
                    <h6>Native Series</h6>
                    <div className="d-flex align-items-center flex-wrap">
                      <div className="selected mb-3 d-flex align-items-center">
                        <FormControl fullWidth>
                          <InputLabel id="native-label">
                            Select a Colour
                          </InputLabel>
                          <Select
                            labelId="native-label"
                            value={doorLogic.selectedColor}
                            label="Native"
                            name="native"
                            onChange={handleColorChange}
                          >
                            {NATIVE_COLORS.map((option) => (
                              <MenuItem key={option.id} value={option.bgcolor}>
                                <ColorLabel
                                  name={option.name}
                                  trademark=""
                                  color={option.bgcolor}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                  </section>
                </>
              )}

              {doorLogic.selectSeries === "Selections Series" &&
                doorLogic.PanelStyle !== "SolidSheet" && (
                  <>
                    <section className="customisingdesktopDoor mt-4 mb-4">
                      <h5 className="mt-3">Select Your Material Colour</h5>
                      <h6>Composite</h6>
                      <div className="d-flex align-items-center flex-wrap">
                        <div className="selected mb-3 d-flex align-items-center">
                          <FormControl fullWidth>
                            <InputLabel id="composite-label">
                              Select a Colour
                            </InputLabel>
                            <Select
                              labelId="composite-label"
                              value={doorLogic.selectedColor}
                              label="Composite"
                              name="composite"
                              onChange={handleColorChange}
                            >
                              {COMPOSITE_COLORS.map((option) => (
                                <MenuItem
                                  key={option.id}
                                  value={option.bgcolor}
                                >
                                  <ColorLabel
                                    name={option.name}
                                    trademark=""
                                    color={option.bgcolor}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </section>
                  </>
                )}

              {doorLogic.selectSeries !== "Selections Series" &&                
                doorLogic.selectedPattern !== "roller" &&
                (doorLogic.selectedDesign === "Madison" ||
                  doorLogic.selectedDesign === "Tuscan") && (
                  <>
                    <section className="customisingdesktopDoor mt-4 mb-4">
                      <h6>Platinum</h6>
                      <div className="d-flex align-items-center flex-wrap">
                        <div className="selected mb-3 d-flex align-items-center">
                          <FormControl fullWidth>
                            <InputLabel id="platinum-label">
                              Select a Colour
                            </InputLabel>
                            <Select
                              labelId="platinum-label"
                              value={doorLogic.selectedColor}
                              label="Platinum"
                              name="platinum"
                              onChange={handleColorChange}
                            >
                              {PLATINUM_COLORS.map((option) => (
                                <MenuItem
                                  key={option.id}
                                  value={option.bgcolor}
                                >
                                  <ColorLabel
                                    name={option.name}
                                    trademark=""
                                    color={option.bgcolor}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </section>
                  </>
                )}

              {/* Window Options */}
              <div className="window-option">
                <h4 className="mb-3 mt-4">Window Option</h4>
                {doorLogic.selectedDesign === "Oxford" ||
                doorLogic.selectedDesign === "Hampton" ? (
                  <select
                    className="selected"
                    value={doorLogic.selctwindow}
                    onChange={handleWindowChange}
                  >
                    <option value="">No Window</option>
                    {HAMPTON_WINDOWS.map((window) => (
                      <option value={window.name} key={window.id}>
                        {window.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Alert severity="error" className="mb-2">
                    Sorry, this model is not available with windows
                  </Alert>
                )}
              </div>

              {/* Save and Contact Buttons */}
              <div className="save-btn-container mt-4">
                {error && (
                  <Alert severity="error" className="mb-2">
                    {error}
                  </Alert>
                )}
                <button
                  className="btn save-btn"
                  onClick={() => {
                    trackVisualiserSave({
                      door_style: doorLogic.pattern || 'Not specified',
                      door_series: doorLogic.series || 'Not specified',
                      door_design: doorLogic.design || 'Not specified',
                      door_colour: doorLogic.color || 'Not specified',
                      panel_style: doorLogic.style || 'Not specified',
                      source: 'mobile_customise'
                    });
                    imageHandling.handleSaveImage();
                  }}
                >
                  Save Image
                </button>
                <button
                  className="btn mt-3 free-qoute"
                  onClick={() => {
                    trackVisualiserQuote({
                      door_style: doorLogic.pattern || 'Not specified',
                      door_series: doorLogic.series || 'Not specified',
                      door_design: doorLogic.design || 'Not specified',
                      door_colour: doorLogic.color || 'Not specified',
                      panel_style: doorLogic.style || 'Not specified',
                      door_width: doorLogic.width || 'Not specified',
                      source: 'mobile_customise'
                    });
                    handleOpenModal();
                  }}
                >
                  CONTACT US FOR FREE QUOTE
                </button>

                {isModalOpen && (
                  <div className="contact-modal-overlay">
                    <div className="contact-modal-content" ref={formRef}>
                      <button
                        className="contact-modal-close"
                        onClick={handleCloseModal}
                        aria-label="Close contact form"
                      >
                        ✕
                      </button>
                      <ContactForm
                        onClose={handleCloseModal}
                        series={doorLogic.selectSeries}
                        design={doorLogic.selectedDesign}
                        color={doorLogic.selectedColorName}
                        style={doorLogic.PanelStyle}
                        pattern={doorLogic.selectedPattern}
                        doorwidth={doorLogic.doorWidth}
                        // Pass additional props for high-res image generation
                        points={doorLogic.points}
                        doorImage={doorLogic.image}
                        bgImage={doorLogic.bgImage}
                        houseImageDimensions={
                          imageHandling.houseImageDimensions
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default CustomisingDoor;
